import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSelectionListChange } from '@angular/material/list';
import { UntilDestroy } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';

export interface ValueOption {
  /**
   * Actual value of this option that is stored in the control.
   */
  value: string;
  /**
   * Optional display text that is shown instead of value.
   */
  displayText?: string;
}

export interface DialogData {
  options: ValueOption[];
  selectedValue: string;
  header: string;
  headerInfo: string;
  headerInfoSmall: string;
  useMultiSelection: boolean;
  selectedOptions: string[];
  minimumSelectionCount: number;
  onNewItem?: (item: string) => void;
  showSelectAllButton?: boolean,
  selectAllText?: string,
  disableAllText?: string,
}

/**
 * A dialog component that shows a list of selectable options. Multi-select is supported by setting useMultiSelection to true.
 * Options are specified as a list of ValueOptions.
 * The returned value upon closing the dialog is either the selected option's value, or in case of multi-select, an array containing
 * the values of the selected options.
 * It is also possible that the dialog result is undefined, which happens if the user pressed escape. Consider handling this as a "no changes" result.
 */
@UntilDestroy()
@Component({
  selector: 'app-list-value-selector',
  templateUrl: './list-value-selector.component.html',
  styleUrls: ['./list-value-selector.component.scss']
})
export class ListValueSelectorComponent {

  public header: string = "";
  public headerInfo: string = "";
  public headerInfoSmall: string = '';
  public options: ValueOption[] = [];
  public showSearch: boolean = false;
  public useMultiSelection: boolean = false;
  public minimumSelectionCount: number = 0;
  public showSelectAllButton: boolean = false;

  public searchText: string = "";
  public selectAllText: string = "";
  public disableAllText: string = "";
  public selectedValue: string = "";
  public selectedOptions: string[] = [];
  private onNewItem?: (item: string) => void;

  constructor(
    public dialogRef: MatDialogRef<ListValueSelectorComponent>,
    private translate: TranslateService,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
  ) {
    this.options = data.options;
    this.useMultiSelection = data.useMultiSelection;
    this.header = data.header;
    this.headerInfo = data.headerInfo;
    this.headerInfoSmall = data.headerInfoSmall;
    this.showSearch = this.options.length >= 10 && !this.useMultiSelection;
    this.selectedValue = data.selectedValue;
    this.selectedOptions = data.selectedOptions;
    this.minimumSelectionCount = data.minimumSelectionCount ?? 0;
    this.showSelectAllButton = data.showSelectAllButton ?? true;
    this.selectAllText = data.selectAllText ?? this.translate.instant('General.SelectAll'),
    this.disableAllText = data.disableAllText ?? this.translate.instant('General.DeselectAll'),
    this.onNewItem = data.onNewItem;
  }

  public canClose(): boolean {
    return !(this.useMultiSelection && this.selectedOptions.length < this.minimumSelectionCount);
  }

  public onNoClick(): void {
    if (this.useMultiSelection) {
      this.dialogRef.close(this.selectedOptions);
    } else {
      this.dialogRef.close(this.selectedValue);
    }
  }

  public onSearchTextChanged(searchText: string): void {
    this.searchText = searchText;
  }

  public setSelectedValue(value: string): void {
    this.selectedValue = value;
    this.dialogRef.close(value);
  }

  public onSelectionChange(event: MatSelectionListChange): void {
    const newOption = event.options
      .filter(option => option.selected)
      .map(option => option.value)[0];

    if (newOption) {
      this.onNewItem?.(newOption);
    }
  }

  public selectOrDisableAll(): void {
    if (this.options.length !== this.selectedOptions.length) {
      this.selectedOptions = this.options.map(opt => opt.value);
    } else {
      this.selectedOptions = [];
    }
  }

  public getSelectAllText(): string {
    return this.options.length === this.selectedOptions.length ? this.disableAllText : this.selectAllText;
  }

  public getSelectAllIcon(): string {
    return this.options.length === this.selectedOptions.length ? 'deselect' : 'select_all';
  }
}
